/**
 * aethereum room create [name] / room list [--local] / room use <id|name> —
 * manage rooms from the CLI.
 *
 *   create        → POST /api/rooms (account token) or /api/anon/room (no account);
 *                   prints the roomId, the copyable connect line, and the web view URL.
 *   list          → GET /api/rooms (account token); prints a tidy id/name/role table.
 *   list --local  → the local projects[] → roomId map from ~/.aethereum/config.json
 *                   (which directory is wired to which room, no network).
 *   use <id|name> → re-wire the CURRENT project to a different owned room: mints a
 *                   fresh member seat via the agents endpoint, then runs the same
 *                   wireProject as init (config, .mcp.json, hooks, editor configs).
 *
 * Fails soft: offline / API-down prints a clear message and exits 1, never crashes.
 */

import { homedir } from "node:os";
import { AccountClient, OfflineError, type RoomSummary } from "../hive/account-client.js";
import { getAccount, readConfig } from "../hive/config-store.js";
import { machineHandle } from "../hive/machine-handle.js";
import { wireProject } from "./init.js";

export interface RoomOptions {
  url?: string;
  token?: string;
  home?: string;
  cwd?: string;
  env?: Record<string, string | undefined>;
  fetchImpl?: typeof fetch;
}

const DEFAULT_BASE = "https://www.aethereum.dev";

function resolveBase(options: RoomOptions): string {
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  return (options.url ?? env["AETHEREUM_SYNC_API_URL"] ?? DEFAULT_BASE)
    .replace(/\/$/, "")
    .replace(/^https:\/\/aethereum\.dev(?=$|\/)/, "https://www.aethereum.dev");
}

/** Account token = flag/env wins, else the stored login token. */
function accountToken(options: RoomOptions, home: string): string | undefined {
  const env = options.env ?? (process.env as Record<string, string | undefined>);
  return options.token ?? env["AETHEREUM_ACCOUNT_TOKEN"] ?? getAccount(home)?.token;
}

function renderRooms(rooms: RoomSummary[]): string {
  if (rooms.length === 0) return "No rooms yet. Run 'aethereum room create' or 'aethereum init'.";
  const head = ["ID", "NAME", "ROLE"];
  const rows = rooms.map((r) => [r.id, r.name || "(unnamed)", r.role || "member"]);
  const widths = head.map((h, i) => Math.max(h.length, ...rows.map((row) => row[i]!.length)));
  const fmt = (row: string[]) => row.map((c, i) => c.padEnd(widths[i]!)).join("  ");
  return [fmt(head), ...rows.map(fmt)].join("\n");
}

export async function runRoom(args: string[], options: RoomOptions = {}): Promise<void> {
  const sub = args[0];
  const home = options.home ?? homedir();
  const base = resolveBase(options);
  const client = new AccountClient(base, options.fetchImpl ?? fetch);

  try {
    if (sub === "create") {
      const name = args
        .slice(1)
        .filter((a) => !a.startsWith("--"))
        .join(" ")
        .trim();
      const token = accountToken(options, home);
      const room = token
        ? await client.createRoom(token, name || undefined)
        : await client.createAnonRoom();

      console.log(`\n  [ok] room created${name ? ` "${name}"` : ""}  -- ${room.roomId}`);
      if (!token) console.log("       (accountless room; run 'aethereum claim' to keep it + invite teammates)");
      console.log(`\n  Watch it:  ${room.viewUrl || `${base}/dashboard`}`);
      if (room.joinCode) {
        console.log(`\n  Bring a teammate in, they run:`);
        console.log(`    npx aethereum join ${room.joinCode}`);
      }
      console.log(`\n  Wire a machine to it:`);
      console.log(`    npx -y aethereum init --token ${room.token}\n`);
      return;
    }

    if (sub === "list") {
      // --local: the projects[] → roomId map from ~/.aethereum/config.json.
      if (args.includes("--local")) {
        const projects = readConfig(home).projects;
        const entries = Object.entries(projects);
        if (entries.length === 0) {
          console.log("\nNo projects wired locally. Run 'aethereum init' in a project.\n");
          return;
        }
        console.log("\nLocal projects (this machine):\n");
        for (const [path, p] of entries) {
          console.log(`  ${path}`);
          console.log(
            `    room ${p.roomId ?? "(id unknown, run 'aethereum reconnect' there)"}${p.anon ? "  · anon (unclaimed)" : ""}  (${p.url})`,
          );
        }
        console.log("");
        return;
      }
      const token = accountToken(options, home);
      if (!token) {
        console.error("'aethereum room list' needs an account. Run 'aethereum login' first (or use 'aethereum room list --local').");
        process.exit(1);
        return;
      }
      const rooms = await client.listRooms(token);
      console.log("\n" + renderRooms(rooms) + "\n");
      return;
    }

    if (sub === "use") {
      const query = args
        .slice(1)
        .filter((a) => !a.startsWith("--"))
        .join(" ")
        .trim();
      if (!query) {
        console.error("Usage: aethereum room use <room-id | room-name>");
        process.exit(1);
        return;
      }
      const token = accountToken(options, home);
      if (!token) {
        console.error("'aethereum room use' needs an account. Run 'aethereum login' first.");
        process.exit(1);
        return;
      }
      const rooms = await client.listRooms(token);
      const byId = rooms.find((r) => r.id === query);
      const byName = rooms.filter((r) => (r.name ?? "").toLowerCase() === query.toLowerCase());
      let target: RoomSummary | undefined = byId;
      if (!target) {
        if (byName.length > 1) {
          console.error(`"${query}" matches ${byName.length} rooms; use the id instead:\n`);
          console.error(renderRooms(byName));
          process.exit(1);
          return;
        }
        target = byName[0];
      }
      if (!target) {
        console.error(`No room "${query}" in your account. 'aethereum room list' shows what you own.`);
        process.exit(1);
        return;
      }

      // Mint a fresh member seat for THIS machine in the target room, then wire
      // the current project exactly the way init does.
      const cwd = options.cwd ?? process.cwd();
      const env = options.env ?? (process.env as Record<string, string | undefined>);
      const { token: roomToken } = await client.addAgent(token, target.id, machineHandle());
      if (!roomToken) {
        console.error("The server did not return a room token; nothing was re-wired.");
        process.exit(1);
        return;
      }
      const wired = wireProject(cwd, home, base, roomToken, env, {
        roomId: target.id,
        anon: target.anon,
      });
      console.log(`\n  [ok] this project now uses room ${target.id}${target.name ? ` ("${target.name}")` : ""}:\n`);
      for (const line of wired.summary) console.log(line);
      console.log("");
      return;
    }

    console.error(
      "Usage: aethereum room create [name] | aethereum room list [--local] | aethereum room use <id|name>",
    );
    process.exit(1);
  } catch (err) {
    if (err instanceof OfflineError) {
      console.error("Could not reach Aethereum (offline?). Try again when you are online.");
    } else {
      console.error(err instanceof Error ? err.message : String(err));
    }
    process.exit(1);
  }
}
